import React from 'react';
import { HStack, Image, Text, VStack, Box, Button, Input, useToast } from '@chakra-ui/react';
import { allBenefitsProduct } from './BenefitsList';

interface Benefit {
  title: string;
  category: string;
  image: string;
  url?: string;
  disabled?: boolean;
  active?: boolean;
}

interface BenefitsSelectorProps {
  onSelectBenefit: (benefit: Benefit | null) => void;
  selectedBenefit?: Benefit | null;
  onCategoryChange?: (category: string) => void;
  currentCategory?: string;
  existingModules?: Array<{ name?: string; id?: number }>;
  customSegments?: string[];
  onAddCustomSegment?: (segment: string) => void;
  onRemoveCustomSegment?: (segment: string) => void;
}

export function BenefitsSelector({
  onSelectBenefit,
  selectedBenefit,
  onCategoryChange,
  currentCategory,
  existingModules = [],
  customSegments = [],
  onAddCustomSegment,
  onRemoveCustomSegment
}: BenefitsSelectorProps) {
  const categories = React.useMemo(() => ['Streaming', 'TVs', 'Telemedicina', '5G'], []);
  const [selectedCategory, setSelectedCategory] = React.useState<string>(categories[0]);
  const [newCategory, setNewCategory] = React.useState('');

  const toast = useToast();

  const benefits = React.useMemo(
    () => {
      let filteredBenefits;
      // Se a categoria selecionada não está nas categorias padrão, mostrar todos os benefícios
      if (selectedCategory && !categories.includes(selectedCategory)) {
        filteredBenefits = allBenefitsProduct;
      } else {
        // Caso contrário, filtrar por categoria
        filteredBenefits = allBenefitsProduct.filter((b) => b.category === selectedCategory);
      }

      // Marcar benefícios que já existem como desabilitados
      return filteredBenefits.map(benefit => ({
        ...benefit,
        disabled: existingModules.some(
          (module) => module.name?.toLowerCase() === benefit.title.toLowerCase()
        )
      }));
    },
    [selectedCategory, categories, existingModules]
  );

  // Sincronizar selectedCategory com currentCategory quando recebido externamente
  React.useEffect(() => {
    if (currentCategory && currentCategory !== selectedCategory) {
      setSelectedCategory(currentCategory);
    }
  }, [currentCategory, selectedCategory]);

  const handleAddCategory = () => {
    if (newCategory.trim()) {
      const category = newCategory.trim();

      // Verificar se já existe um módulo com este nome
      const existingModule = existingModules.find(
        (module) => module.name?.toLowerCase() === category.toLowerCase()
      );

      if (existingModule) {
        toast({
          title: 'Já existe um módulo com este nome',
          status: 'error',
          duration: 3000,
          isClosable: true,
        });
        return;
      }

      // Verificar se já existe nos segmentos personalizados
      if (customSegments.includes(category)) {
        return;
      }

      // Adicionar aos segmentos personalizados
      if (onAddCustomSegment) {
        onAddCustomSegment(category);
      }

      // Selecionar o novo segmento
      onCategoryChange?.(category);
      setSelectedCategory(category);
      setNewCategory('');
    }
  };

  const handleCategoryKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      handleAddCategory();
    } else if (e.key === 'Escape') {
      setNewCategory('');
    }
  };

  return (
    <VStack spacing={2} align="stretch" width="100%">
      <VStack spacing={1} align="stretch">
        <Text fontSize={14} fontWeight="semibold" color="gray.800">
          Alguns Svas mais comuns no mercado
        </Text>
        <Text fontSize="xs" color="gray.600">
          Escolha um Sva para adicionar como benefício, você pode alterar a imagem e o nome do benefício.
        </Text>
      </VStack>

      {/* Segmentos - Lista vertical clicável */}
      <VStack spacing={1.5} align="stretch" width="100%">
        <Text fontSize={12} fontWeight="semibold" color="gray.700" px={1.5}>
          Categorias
        </Text>

        {/* Categorias padrão */}
        {categories.map((cat) => (
          <VStack key={cat} spacing={1} align="stretch" width="100%">
            <Box
              as="button"
              type="button"
              width="100%"
              py={2}
              px={2.5}
              borderRadius="md"
              border="2px solid"
              borderColor={selectedCategory === cat ? 'blue.400' : 'gray.200'}
              bg={selectedCategory === cat ? 'blue.50' : 'white'}
              cursor="pointer"
              transition="all 0.2s"
              textAlign="left"
              _hover={{
                borderColor: 'blue.300',
                bg: selectedCategory === cat ? 'blue.100' : 'gray.50',
                transform: 'translateX(2px)',
                boxShadow: 'sm',
              }}
              _active={{
                transform: 'translateX(0px)',
              }}
              onClick={() => {
                setSelectedCategory(cat);
                onCategoryChange?.(cat);
              }}
            >
              <HStack justify="space-between" width="100%">
                <Text
                  fontWeight={selectedCategory === cat ? 'semibold' : 'medium'}
                  color={selectedCategory === cat ? 'blue.700' : 'gray.700'}
                  fontSize="sm"
                >
                  {cat}
                </Text>
                <HStack spacing={1}>
                  <Box
                    as="span"
                    fontSize="12px"
                    color={selectedCategory === cat ? 'blue.600' : 'gray.400'}
                    transform={selectedCategory === cat ? 'rotate(0deg)' : 'rotate(-90deg)'}
                    transition="transform 0.2s"
                  >
                    ▼
                  </Box>
                </HStack>
              </HStack>
            </Box>

            {/* Lista de benefícios para esta categoria */}
            {selectedCategory === cat && (
              <Box
                width="100%"
                overflowX="auto"
                paddingY={0.5}
                pl={1}
                css={{
                  '&::-webkit-scrollbar': {
                    height: '3px',
                  },
                  '&::-webkit-scrollbar-track': {
                    background: '#f1f1f1',
                    borderRadius: '2px',
                  },
                  '&::-webkit-scrollbar-thumb': {
                    background: '#c1c1c1',
                    borderRadius: '2px',
                  },
                  '&::-webkit-scrollbar-thumb:hover': {
                    background: '#a8a8a8',
                  },
                }}
              >
                <HStack spacing={1.5} minW="max-content" width="100%">
                  {benefits.map((benefit, index) => (
                    <VStack
                      key={index}
                      cursor={benefit.disabled ? 'not-allowed' : 'pointer'}
                      padding={1.5}
                      borderRadius="10px"
                      border="1px solid"
                      borderColor={
                        benefit.disabled
                          ? 'gray.300'
                          : selectedBenefit?.title === benefit.title
                            ? 'blue.400'
                            : 'gray.200'
                      }
                      backgroundColor={
                        benefit.disabled
                          ? 'gray.100'
                          : selectedBenefit?.title === benefit.title
                            ? 'blue.50'
                            : 'white'
                      }
                      boxShadow={selectedBenefit?.title === benefit.title ? 'md' : 'xs'}
                      opacity={benefit.disabled ? 0.6 : 1}
                      _hover={benefit.disabled ? {} : {
                        borderColor: 'blue.300',
                        backgroundColor: 'blue.50',
                        transform: 'translateY(-1px)',
                        boxShadow: 'md',
                      }}
                      onClick={() => {
                        if (selectedBenefit?.title === benefit.title) {
                          onSelectBenefit(null);
                        } else {
                          const existingModule = existingModules.find(
                            (module) => module.name?.toLowerCase() === benefit.title.toLowerCase()
                          );

                          if (existingModule) {
                            return;
                          }

                          onSelectBenefit(benefit);
                        }
                      }}
                      minW="70px"
                      maxW="70px"
                      height="80px"
                      flexShrink={0}
                      transition="all 0.2s ease"
                      position="relative"
                      overflow="hidden"
                      justifyContent="space-between"
                    >
                      {selectedBenefit?.title === benefit.title && (
                        <Box
                          position="absolute"
                          top={1}
                          right={1}
                          width="14px"
                          height="14px"
                          borderRadius="full"
                          backgroundColor="blue.400"
                          display="flex"
                          alignItems="center"
                          justifyContent="center"
                          zIndex={1}
                        >
                          <Box
                            width="3px"
                            height="3px"
                            borderRadius="full"
                            backgroundColor="white"
                          />
                        </Box>
                      )}

                      {benefit.disabled && (
                        <Box
                          position="absolute"
                          top={1}
                          left={1}
                          width="14px"
                          height="14px"
                          borderRadius="full"
                          backgroundColor="red.400"
                          display="flex"
                          alignItems="center"
                          justifyContent="center"
                          zIndex={1}
                        >
                          <Text fontSize="7px" color="white" fontWeight="bold">
                            !
                          </Text>
                        </Box>
                      )}

                      <VStack spacing={0.5} flex={1} justify="center">
                        <Box
                          width="45px"
                          height="45px"
                          borderRadius="8px"
                          overflow="hidden"
                          backgroundColor="gray.50"
                          display="flex"
                          alignItems="center"
                          justifyContent="center"
                          boxShadow="xs"
                        >
                          <Image
                            src={benefit.image}
                            alt={benefit.title}
                            width="100%"
                            height="100%"
                            objectFit="cover"
                            fallbackSrc="/assets/default.png"
                          />
                        </Box>

                        <Text
                          fontSize="10px"
                          textAlign="center"
                          fontWeight="semibold"
                          color={
                            benefit.disabled
                              ? 'gray.500'
                              : selectedBenefit?.title === benefit.title
                                ? 'blue.600'
                                : 'gray.800'
                          }
                          noOfLines={1}
                          width="100%"
                          overflow="hidden"
                          textOverflow="ellipsis"
                          whiteSpace="nowrap"
                          lineHeight="1"
                        >
                          {benefit.disabled ? `${benefit.title}` : benefit.title}
                        </Text>
                      </VStack>
                    </VStack>
                  ))}
                </HStack>
              </Box>
            )}
          </VStack>
        ))}

        {/* Segmentos personalizados */}
        {customSegments.length > 0 && (
          <>
            <Text fontSize={11} fontWeight="semibold" color="gray.700" px={1.5} mt={1}>
              Segmentos Personalizados
            </Text>
            {customSegments.map((segment) => (
              <VStack key={segment} spacing={1} align="stretch" width="100%">
                <HStack
                  width="100%"
                  spacing={0}
                  py={2}
                  px={2.5}
                  borderRadius="md"
                  border="2px solid"
                  borderColor={selectedCategory === segment ? 'green.400' : 'gray.200'}
                  bg={selectedCategory === segment ? 'green.50' : 'white'}
                  cursor="pointer"
                  transition="all 0.2s"
                  _hover={{
                    borderColor: 'green.300',
                    bg: selectedCategory === segment ? 'green.100' : 'gray.50',
                    transform: 'translateX(2px)',
                    boxShadow: 'sm',
                  }}
                  onClick={() => {
                    setSelectedCategory(segment);
                    onCategoryChange?.(segment);
                  }}
                >
                  <VStack align="start" flex={1} spacing={0}>
                    <HStack justify="space-between" width="100%">
                      <Text
                        flex={1}
                        fontWeight={selectedCategory === segment ? 'semibold' : 'medium'}
                        color={selectedCategory === segment ? 'green.700' : 'gray.700'}
                        fontSize="sm"
                      >
                        {segment}
                      </Text>
                      <Box
                        as="span"
                        fontSize="12px"
                        color={selectedCategory === segment ? 'green.600' : 'gray.400'}
                        transform={selectedCategory === segment ? 'rotate(0deg)' : 'rotate(-90deg)'}
                        transition="transform 0.2s"
                        ml={2}
                      >
                        ▼
                      </Box>
                    </HStack>
                  </VStack>

                  {onRemoveCustomSegment && (
                    <Button
                      size="xs"
                      variant="ghost"
                      colorScheme="red"
                      p={0.5}
                      minW="auto"
                      h="auto"
                      fontSize="md"
                      onClick={(e) => {
                        e.stopPropagation();
                        onRemoveCustomSegment(segment);
                        if (selectedCategory === segment) {
                          setSelectedCategory(categories[0]);
                          onCategoryChange?.(categories[0]);
                        }
                      }}
                      _hover={{ bg: 'red.100', color: 'red.600' }}
                    >
                      ×
                    </Button>
                  )}
                </HStack>

                {/* Lista de benefícios para este segmento */}
                {selectedCategory === segment && (
                  <Box
                    width="100%"
                    overflowX="auto"
                    paddingY={0.5}
                    pl={1}
                    css={{
                      '&::-webkit-scrollbar': {
                        height: '3px',
                      },
                      '&::-webkit-scrollbar-track': {
                        background: '#f1f1f1',
                        borderRadius: '2px',
                      },
                      '&::-webkit-scrollbar-thumb': {
                        background: '#c1c1c1',
                        borderRadius: '2px',
                      },
                      '&::-webkit-scrollbar-thumb:hover': {
                        background: '#a8a8a8',
                      },
                    }}
                  >
                    <HStack spacing={1.5} minW="max-content" width="100%">
                      {benefits.map((benefit, index) => (
                        <VStack
                          key={index}
                          cursor={benefit.disabled ? 'not-allowed' : 'pointer'}
                          padding={1.5}
                          borderRadius="10px"
                          border="1px solid"
                          borderColor={
                            benefit.disabled
                              ? 'gray.300'
                              : selectedBenefit?.title === benefit.title
                                ? 'blue.400'
                                : 'gray.200'
                          }
                          backgroundColor={
                            benefit.disabled
                              ? 'gray.100'
                              : selectedBenefit?.title === benefit.title
                                ? 'blue.50'
                                : 'white'
                          }
                          boxShadow={selectedBenefit?.title === benefit.title ? 'md' : 'xs'}
                          opacity={benefit.disabled ? 0.6 : 1}
                          _hover={benefit.disabled ? {} : {
                            borderColor: 'blue.300',
                            backgroundColor: 'blue.50',
                            transform: 'translateY(-1px)',
                            boxShadow: 'md',
                          }}
                          onClick={() => {
                            if (selectedBenefit?.title === benefit.title) {
                              onSelectBenefit(null);
                            } else {
                              const existingModule = existingModules.find(
                                (module) => module.name?.toLowerCase() === benefit.title.toLowerCase()
                              );

                              if (existingModule) {
                                return;
                              }

                              onSelectBenefit(benefit);
                            }
                          }}
                          minW="70px"
                          maxW="70px"
                          height="80px"
                          flexShrink={0}
                          transition="all 0.2s ease"
                          position="relative"
                          overflow="hidden"
                          justifyContent="space-between"
                        >
                          {selectedBenefit?.title === benefit.title && (
                            <Box
                              position="absolute"
                              top={1}
                              right={1}
                              width="14px"
                              height="14px"
                              borderRadius="full"
                              backgroundColor="blue.400"
                              display="flex"
                              alignItems="center"
                              justifyContent="center"
                              zIndex={1}
                            >
                              <Box
                                width="3px"
                                height="3px"
                                borderRadius="full"
                                backgroundColor="white"
                              />
                            </Box>
                          )}

                          {benefit.disabled && (
                            <Box
                              position="absolute"
                              top={1}
                              left={1}
                              width="14px"
                              height="14px"
                              borderRadius="full"
                              backgroundColor="red.400"
                              display="flex"
                              alignItems="center"
                              justifyContent="center"
                              zIndex={1}
                            >
                              <Text fontSize="7px" color="white" fontWeight="bold">
                                !
                              </Text>
                            </Box>
                          )}

                          <VStack spacing={0.5} flex={1} justify="center">
                            <Box
                              width="45px"
                              height="45px"
                              borderRadius="8px"
                              overflow="hidden"
                              backgroundColor="gray.50"
                              display="flex"
                              alignItems="center"
                              justifyContent="center"
                              boxShadow="xs"
                            >
                              <Image
                                src={benefit.image}
                                alt={benefit.title}
                                width="100%"
                                height="100%"
                                objectFit="cover"
                                fallbackSrc="/assets/default.png"
                              />
                            </Box>

                            <Text
                              fontSize="10px"
                              textAlign="center"
                              fontWeight="semibold"
                              color={
                                benefit.disabled
                                  ? 'gray.500'
                                  : selectedBenefit?.title === benefit.title
                                    ? 'blue.600'
                                    : 'gray.800'
                              }
                              noOfLines={1}
                              width="100%"
                              overflow="hidden"
                              textOverflow="ellipsis"
                              whiteSpace="nowrap"
                              lineHeight="1"
                            >
                              {benefit.disabled ? `${benefit.title}` : benefit.title}
                            </Text>
                          </VStack>
                        </VStack>
                      ))}
                    </HStack>
                  </Box>
                )}
              </VStack>
            ))}
          </>
        )}

        {/* Campo para adicionar segmento */}
        <Box py={2} px={2.5} borderRadius="md" border="2px dashed" borderColor="blue.300" bg="blue.50">
          <Text fontSize={11} fontWeight="semibold" color="blue.700" mb={1.5}>
            + Novo segmento
          </Text>
          <HStack spacing={1.5} width="100%">
            <Input
              placeholder="Digite o segmento"
              size="sm"
              value={newCategory}
              onChange={(e) => setNewCategory(e.target.value)}
              onKeyDown={handleCategoryKeyPress}
              width="100%"
              border="1px solid"
              borderColor="blue.300"
              bg="white"
              height="32px"
              fontSize="sm"
              _focus={{
                borderColor: 'blue.500',
                boxShadow: '0 0 0 1px var(--chakra-colors-blue-500)',
              }}
            />
            <Button
              size="sm"
              colorScheme="blue"
              onClick={handleAddCategory}
              isDisabled={!newCategory.trim()}
              minW="65px"
              height="32px"
              fontSize="sm"
            >
              Adicionar
            </Button>
          </HStack>
        </Box>
      </VStack>
    </VStack>
  );
}
